import React from "react";
import { Search, Building2, User2 } from "lucide-react";

const LeadShuffleLoader = ({ title, subtitle }) => {
  const messages = [
    "Shuffling through thousands of companies for you…",
    "Pairing the right people with the right accounts…",
    "Dropping weak fits, keeping only the sharp ones…",
    "Checking fresh data so your list isn’t stale…",
  ];

  // NEW: rotating card content (no hard-coded company names)
  const cardStates = [
    {
      label: "Strong account match",
      company: "SaaS · 51–200 employees · US",
      person: "VP Sales · Outbound focused",
    },
    {
      label: "Saved as high-intent",
      company: "Fintech · 200–500 · Series B",
      person: "Head of Revenue · EU",
    },
    {
      label: "Fresh buying signal",
      company: "HR Tech · 1k–5k employees",
      person: "Director of Sales ·North America",
    },
    {
      label: "Warm outbound candidate",
      company: "B2B · 200–500 · Global",
      person: "Growth Lead · Remote",
    },
  ];

  const [messageIndex, setMessageIndex] = React.useState(0);
  const [cardIndex, setCardIndex] = React.useState(0);

  React.useEffect(() => {
    const msgTimer = setInterval(
      () => setMessageIndex((i) => (i + 1) % messages.length),
      2500
    );
    return () => clearInterval(msgTimer);
  }, [messages.length]);

  // NEW: rotate which card text is used
  React.useEffect(() => {
    const cardTimer = setInterval(
      () => setCardIndex((i) => (i + 1) % cardStates.length),
      2600
    );
    return () => clearInterval(cardTimer);
  }, [cardStates.length]);

  const activeCard = cardStates[cardIndex];
  const nextCard = cardStates[(cardIndex + 1) % cardStates.length];

  return (
    <div className="flex items-center justify-center min-h-[420px] sm:min-h-[500px] px-4 py-8 relative overflow-hidden">
      {/* soft background accents */}
      <div className="pointer-events-none absolute -top-32 left-[-5%] w-64 h-64 rounded-full blur-3xl" />

      <div className="relative w-full max-w-xl rounded-3xl shadow-slate-200/70 backdrop-blur-md px-5 py-7 sm:px-7 sm:py-8">
        {/* title + subtitle */}
        <div className="text-center max-w-md mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight">
            {title || "Sit back - we’re curating your best leads"}
          </h2>
          <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
            {subtitle ||
              "We’re sifting through accounts and people so you only see the ones that actually matter."}
          </p>
        </div>

        {/* main animation */}
        <div className="mt-6 sm:mt-8 flex justify-center">
          <div className="relative w-96 h-44 sm:w-72 sm:h-48">
            {/* glow behind cards */}
            <div className="absolute inset-0 rounded-[28px] bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25)_0,_transparent_55%)] blur-xl" />

            {/* card stack */}
            <div className="absolute inset-0 flex items-center justify-center px-5">
              {/* bottom faint card */}
              <LeadCard
                className="translate-y-6 scale-[0.9] opacity-60"
                subtle
                label="Browsing ICP fit…"
                company="~12,000 accounts"
                person="Filtering…"
              />

              {/* middle card animating (dynamic content) */}
              <div className="absolute animate-card-middle px-5">
                <LeadCard
                  label={activeCard.label}
                  company={activeCard.company}
                  person={activeCard.person}
                />
              </div>

              {/* top card leaves and re-enters like shuffle (also dynamic) */}
              <div className="absolute animate-card-top">
                <LeadCard
                  accent
                  label={nextCard.label}
                  company={nextCard.company}
                  person={nextCard.person}
                />
              </div>
            </div>
          </div>
        </div>

        {/* rotating message + progress */}
        <div className="mt-8 space-y-3">
          <p className="text-center text-[11px] sm:text-xs text-slate-500 min-h-[22px] animate-fade-message">
            {messages[messageIndex]}
          </p>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-[11px] sm:text-xs font-medium text-slate-500">
                Building your result list…
              </span>
              <span className="text-[11px] sm:text-xs text-slate-400">
                Usually under a minute
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 animate-indeterminate" />
            </div>
          </div>
        </div>

        {/* animations */}
        <style jsx>{`
          @keyframes indeterminate {
            0% {
              transform: translateX(-100%);
            }
            50% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(100%);
            }
          }

          @keyframes fade-message {
            0% {
              opacity: 0;
              transform: translateY(2px);
            }
            15% {
              opacity: 1;
              transform: translateY(0);
            }
            85% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-2px);
            }
          }

          /* middle card gentle hover */
          @keyframes card-middle {
            0%,
            100% {
              transform: translateY(4px) scale(0.97);
            }
            50% {
              transform: translateY(-4px) scale(1);
            }
          }

          /* top card shuffling loop */
          @keyframes card-top {
            0% {
              transform: translateY(-8px) scale(1) rotate(0deg);
              opacity: 1;
            }
            30% {
              transform: translateY(-16px) scale(1.02) rotate(-1deg);
              opacity: 1;
            }
            50% {
              transform: translateY(-40px) scale(0.98) rotate(3deg);
              opacity: 0;
            }
            51% {
              transform: translateY(40px) scale(0.9) rotate(-3deg);
              opacity: 0;
            }
            80% {
              transform: translateY(-2px) scale(1.01) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(-8px) scale(1) rotate(0deg);
              opacity: 1;
            }
          }

          .animate-indeterminate {
            animation: indeterminate 1.8s ease-in-out infinite;
          }
          .animate-fade-message {
            animation: fade-message 2.5s ease-in-out infinite;
          }
          .animate-card-middle {
            animation: card-middle 3s ease-in-out infinite;
          }
          .animate-card-top {
            animation: card-top 4s ease-in-out infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

const LeadCard = ({
  label,
  company,
  person,
  subtle = false,
  accent = false,
  className = "",
}) => {
  return (
    <div
      className={`relative w-72 sm:w-72 rounded-2xl border px-6 py-3.5 sm:px-5 sm:py-4 shadow-md backdrop-blur-md
      ${
        accent
          ? "bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-slate-50 border-blue-500 shadow-blue-500/50"
          : subtle
          ? "bg-white/60 text-slate-700 border-slate-200/70"
          : "bg-white text-slate-800 border-slate-200 shadow-slate-200/70"
      }
      ${className}`}
    >
      <div className="flex items-center gap-2 text-[11px] sm:text-xs font-medium">
        <span
          className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5
          ${
            accent
              ? "bg-white/15 text-slate-50"
              : "bg-blue-50 text-blue-600 border border-blue-100"
          }`}
        >
          {label}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-full
          ${accent ? "bg-slate-900/40" : "bg-slate-900/5 text-slate-700"}`}
        >
          <Building2 className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1">
          <p className="font-semibold truncate">{company}</p>
          <p
            className={`${
              accent ? "text-slate-100/80" : "text-slate-500"
            } text-[11px] sm:text-xs`}
          >
            Account
          </p>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-full
          ${accent ? "bg-slate-900/40" : "bg-slate-900/5 text-slate-700"}`}
        >
          <User2 className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1">
          <p className="font-medium truncate">{person}</p>
          <p
            className={`${
              accent ? "text-slate-100/80" : "text-slate-500"
            } text-[11px] sm:text-xs`}
          >
            Contact
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeadShuffleLoader;
